import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileCheck2,
  FileInput,
  History,
  Landmark,
  Link2,
  LoaderCircle,
  Search,
  ShieldCheck,
  UploadCloud
} from "lucide-react";
import { Field, SelectField } from "../components/Form";
import { PageHeader, SectionCard } from "../components/Page";
import { StatusBadge } from "../components/StatusBadge";
import { api, post } from "../lib/api";
import { currency, dateBr } from "../lib/format";

type Notify = (message: string, tone?: "success" | "error") => void;
type Group = "reconciled" | "bankWithoutSystem" | "systemWithoutBank";

export function SimpleBankReconciliationPage({ notify }: { notify: Notify }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [imports, setImports] = useState<any[]>([]);
  const [accountId, setAccountId] = useState(() => sessionStorage.getItem("cce-ofx-account") || "");
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [current, setCurrent] = useState<any | null>(null);
  const [summary, setSummary] = useState<any | null>(null);
  const [group, setGroup] = useState<Group>("reconciled");
  const [locatedEntry, setLocatedEntry] = useState<string | null>(null);
  const [busy, setBusy] = useState("");

  const loadBase = useCallback(async () => {
    const [accountData, importData] = await Promise.all([
      api<any>("/finance/reconciliation/accounts"),
      api<any>("/finance/reconciliation/imports?limit=20")
    ]);
    setAccounts(accountData.items || []);
    setImports(importData.items || []);
    setAccountId((selected) => selected || accountData.items?.[0]?.id || "");
  }, []);

  const openImport = useCallback(async (id: string) => {
    setBusy("open");
    try {
      const [item, simpleSummary] = await Promise.all([
        api<any>(`/finance/reconciliation/imports/${id}`),
        api<any>(`/finance/reconciliation/imports/${id}/simple-summary`)
      ]);
      setCurrent(item);
      setSummary(simpleSummary);
      setAccountId(item.bankAccountId);
      setGroup("reconciled");
      setLocatedEntry(null);
    } catch (error: any) {
      notify(error.message, "error");
    } finally {
      setBusy("");
    }
  }, [notify]);

  useEffect(() => { void loadBase(); }, [loadBase]);

  const upload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accountId || !file) return notify("Selecione a conta financeira e o arquivo OFX.", "error");
    setBusy("upload");
    try {
      const body = new FormData();
      body.append("bankAccountId", accountId);
      body.append("file", file);
      if (notes.trim()) body.append("notes", notes.trim());
      const item = await api<any>("/finance/reconciliation/imports", { method: "POST", body });
      setFile(null);
      setNotes("");
      if (inputRef.current) inputRef.current.value = "";
      await Promise.all([loadBase(), openImport(item.id)]);
      notify("Extrato importado para revisão. Nenhum lançamento foi efetivado automaticamente.");
    } catch (error: any) {
      notify(error.message, "error");
    } finally {
      setBusy("");
    }
  };

  const applyResult = async (result: any, message: string) => {
    setSummary(result.summary);
    if (current) {
      const item = await api<any>(`/finance/reconciliation/imports/${current.id}`);
      setCurrent(item);
    }
    await loadBase();
    notify(message);
  };

  const updateInitialBalance = async () => {
    if (!current || !summary) return;
    if (!window.confirm(`O saldo inicial do CCE Gestão será alterado de ${currency(summary.initialBalance.cceCents)} para ${currency(summary.initialBalance.bankCents)}, sem criar receita, despesa ou movimentação. Confirmar?`)) return;
    setBusy("initial");
    try {
      const result = await post<any>(`/finance/reconciliation/imports/${current.id}/update-initial-balance`, { confirmation: "ATUALIZAR_SALDO_INICIAL" });
      await applyResult(result, "Saldo inicial atualizado pelo extrato e registrado na auditoria.");
    } catch (error: any) { notify(error.message, "error"); }
    finally { setBusy(""); }
  };

  const autoReconcile = async () => {
    if (!current) return;
    if (!window.confirm("Vincular somente as correspondências exatas, de mesma conta, valor e data, sem concorrentes?")) return;
    setBusy("auto");
    try {
      const result = await post<any>(`/finance/reconciliation/imports/${current.id}/auto-reconcile`, { confirmation: "CONCILIAR_CORRESPONDENCIAS_EXATAS" });
      await applyResult(result, `${result.linked.length} correspondência(s) exata(s) vinculada(s), sem recriar lançamentos.`);
    } catch (error: any) { notify(error.message, "error"); }
    finally { setBusy(""); }
  };

  const manualLink = async (entryId: string, movementId: string) => {
    if (!current || !window.confirm("Vincular este movimento do extrato ao lançamento existente selecionado?")) return;
    setBusy(`link:${entryId}`);
    try {
      const result = await post<any>(`/finance/reconciliation/imports/${current.id}/manual-link`, { entryId, movementId });
      await applyResult(result, "Movimento vinculado e conciliado sem criar nova baixa.");
    } catch (error: any) { notify(error.message, "error"); }
    finally { setBusy(""); }
  };

  const createPending = async (entryId: string) => {
    if (!current || !window.confirm("Criar um único lançamento bancário pendente de classificação para esta linha do OFX?")) return;
    setBusy(`pending:${entryId}`);
    try {
      const result = await post<any>(`/finance/reconciliation/imports/${current.id}/entries/${entryId}/create-pending`, { confirmation: "CRIAR_LANCAMENTO_PENDENTE" });
      await applyResult(result, "Lançamento bancário criado como pendente de classificação.");
    } catch (error: any) { notify(error.message, "error"); }
    finally { setBusy(""); }
  };

  const finish = async () => {
    if (!current || !window.confirm("Finalizar a conciliação deste extrato?")) return;
    setBusy("finish");
    try {
      const result = await post<any>(`/finance/reconciliation/imports/${current.id}/finish-simple`, { confirmation: "FINALIZAR_CONCILIACAO" });
      await applyResult(result, "Conciliação finalizada com saldo bancário e CCE iguais.");
    } catch (error: any) { notify(error.message, "error"); }
    finally { setBusy(""); }
  };

  const selectedItems = summary?.groups?.[group] || [];

  return <div className="page bank-reconciliation-page">
    <PageHeader
      eyebrow="Financeiro · Conciliação bancária · Importar extrato"
      title="Conciliação bancária"
      description="Compare o extrato com os movimentos já realizados, vincule correspondências e identifique somente o que falta."
      actions={<button className="button button-primary ofx-highlight" onClick={() => inputRef.current?.click()}><UploadCloud size={18}/>Importar extrato OFX</button>}
    />

    <div className="reconciliation-layout">
      <div className="reconciliation-main">
        <SectionCard title="Importar novo extrato" description="O upload apenas lê o OFX. A conta selecionada será a conta bancária de todas as linhas deste extrato.">
          <form className="ofx-upload-form" onSubmit={upload}>
            <SelectField label="Conta financeira" required value={accountId} onChange={(event) => {
              setAccountId(event.target.value);
              sessionStorage.setItem("cce-ofx-account", event.target.value);
            }}>
              <option value="">Selecione uma conta</option>
              {accounts.map((item) => <option key={item.id} value={item.id}>{item.code} · {item.name || item.institution} · {item.accountNumber || "sem número"}</option>)}
            </SelectField>
            <label className={`ofx-dropzone ${file ? "has-file" : ""}`}>
              <input ref={inputRef} type="file" accept=".ofx,application/x-ofx,application/ofx,text/plain" onChange={(event) => setFile(event.target.files?.[0] || null)}/>
              {file ? <FileCheck2 size={28}/> : <FileInput size={28}/>}
              <span><strong>{file ? file.name : "Anexar arquivo OFX"}</strong><small>{file ? "Pronto para leitura" : "Clique para selecionar o arquivo"}</small></span>
            </label>
            <Field label="Observação (opcional)" value={notes} onChange={(event) => setNotes(event.target.value)}/>
            <button className="button button-primary" disabled={!accountId || !file || busy === "upload"}>{busy === "upload" ? <LoaderCircle className="spin" size={17}/> : <ShieldCheck size={17}/>}Ler extrato</button>
          </form>
        </SectionCard>

        {current && summary && <>
          <SectionCard
            title={`${summary.account.code} · ${summary.account.name}`}
            description={`${dateBr(summary.period.from)} a ${dateBr(summary.period.to)} · ${current.originalName}`}
            actions={<StatusBadge status={current.status}/>}
          >
            <div className="simple-reconciliation-actions">
              <button className="button button-secondary" onClick={() => void autoReconcile()} disabled={Boolean(busy)}><Link2 size={16}/>Conciliar automaticamente</button>
              {summary.initialBalance.differenceCents !== 0 && <button className="button button-accent" onClick={() => void updateInitialBalance()} disabled={Boolean(busy) || !summary.initialBalance.safeToUpdate}>Atualizar saldo inicial pelo extrato</button>}
              <button className="button button-primary" onClick={() => void finish()} disabled={Boolean(busy) || !summary.canFinish}><CheckCircle2 size={16}/>Finalizar conciliação</button>
            </div>

            <div className="difference-ledgers simple-ledgers">
              <section><h4>BANCO</h4><span>Saldo inicial<strong>{currency(summary.bank.initialBalanceCents)}</strong></span><span>Créditos<strong className="text-success">{currency(summary.bank.creditsCents)}</strong></span><span>Débitos<strong className="text-danger">{currency(summary.bank.debitsCents)}</strong></span><span>Saldo final<strong>{currency(summary.bank.finalBalanceCents)}</strong></span></section>
              <section><h4>CCE GESTÃO</h4><span>Saldo inicial<strong>{currency(summary.cce.initialBalanceCents)}</strong></span><span>Créditos<strong className="text-success">{currency(summary.cce.creditsCents)}</strong></span><span>Débitos<strong className="text-danger">{currency(summary.cce.debitsCents)}</strong></span><span>Saldo final<strong>{currency(summary.cce.finalBalanceCents)}</strong></span></section>
              <article className={summary.differenceCents === 0 ? "balanced" : ""}><span>Diferença final</span><strong>{currency(summary.differenceCents)}</strong><small>{summary.differenceCents === 0 ? "Saldos conferidos" : "Veja abaixo os itens exatos"}</small></article>
            </div>

            {summary.initialBalance.differenceCents !== 0 && <div className="ofx-warning-panel">
              <AlertTriangle size={19}/><div><p><strong>O saldo inicial do CCE difere do extrato.</strong></p><p>Banco: {currency(summary.initialBalance.bankCents)} · CCE: {currency(summary.initialBalance.cceCents)}.</p>{summary.initialBalance.calculation && <p>Cálculo seguro: saldo final − créditos + débitos.</p>}{summary.initialBalance.blockingReason && <p>{summary.initialBalance.blockingReason}</p>}</div>
            </div>}

            <div className="difference-counters simple-counters">
              <span>Movimentos do extrato<strong>{summary.totals.statementMovements}</strong></span>
              <span>Correspondências encontradas<strong>{summary.totals.correspondencesFound}</strong></span>
              <span>Conciliados<strong>{summary.totals.reconciledMovements}</strong></span>
              <span>Banco sem correspondência<strong>{summary.totals.bankWithoutSystem}</strong></span>
              <span>CCE sem banco<strong>{summary.totals.systemWithoutBank}</strong></span>
            </div>
          </SectionCard>

          <SectionCard title="Movimentos" description="A conciliação possui somente três grupos. Classificações gerenciais dos lançamentos existentes não são refeitas aqui.">
            <div className="simple-reconciliation-tabs" role="tablist">
              <button role="tab" aria-selected={group === "reconciled"} className={`button ${group === "reconciled" ? "button-primary" : "button-secondary"}`} onClick={() => setGroup("reconciled")}>CONCILIADOS ({summary.groups.reconciled.length})</button>
              <button role="tab" aria-selected={group === "bankWithoutSystem"} className={`button ${group === "bankWithoutSystem" ? "button-primary" : "button-secondary"}`} onClick={() => setGroup("bankWithoutSystem")}>BANCO SEM CORRESPONDÊNCIA ({summary.groups.bankWithoutSystem.length})</button>
              <button role="tab" aria-selected={group === "systemWithoutBank"} className={`button ${group === "systemWithoutBank" ? "button-primary" : "button-secondary"}`} onClick={() => setGroup("systemWithoutBank")}>CCE SEM CORRESPONDÊNCIA ({summary.groups.systemWithoutBank.length})</button>
            </div>

            <div className="simple-reconciliation-list">
              {group === "reconciled" && selectedItems.map((item: any) => <article key={item.entry.id}>
                <MovementSummary movement={item.entry} label="Banco"/>
                <span className="simple-link"><Link2 size={15}/><small>Vinculado</small></span>
                <MovementSummary movement={item.movement} label="CCE Gestão"/>
              </article>)}

              {group === "bankWithoutSystem" && selectedItems.map((item: any) => <article key={item.entry.id} className="simple-pending-row">
                <MovementSummary movement={item.entry} label="Banco"/>
                <div className="simple-pending-actions">
                  <button className="button button-small button-secondary" onClick={() => setLocatedEntry(locatedEntry === item.entry.id ? null : item.entry.id)}><Search size={14}/>Localizar<ChevronDown size={13}/></button>
                  <button className="button button-small button-secondary" onClick={() => item.candidate && void manualLink(item.entry.id, item.candidate.id)} disabled={!item.candidate || busy === `link:${item.entry.id}`}><Link2 size={14}/>Vincular manualmente</button>
                  <button className="button button-small button-primary" onClick={() => void createPending(item.entry.id)} disabled={busy === `pending:${item.entry.id}`}>Criar pendente</button>
                </div>
                {item.match && <div className={`simple-match-note ${item.match.sameValue ? "" : "has-difference"}`}>
                  <strong>{item.match.unique && item.match.sameValue ? "Correspondência provável" : item.match.sameValue ? "Há mais de uma opção compatível" : "Valor diferente"}</strong>
                  <span>{item.candidate ? `${item.candidate.description} · ${currency(item.candidate.amountCents)}` : "Nenhum candidato selecionado"}</span>
                  {!item.match.sameValue && <small>Diferença: {currency(item.match.differenceCents)}. Corrija antes de vincular.</small>}
                </div>}
                {locatedEntry === item.entry.id && <div className="simple-candidates">
                  <strong>Lançamentos compatíveis encontrados</strong>
                  {item.candidates.map((candidate: any) => <div key={candidate.id}><MovementSummary movement={candidate} label="CCE Gestão"/><button className="button button-small button-primary" onClick={() => void manualLink(item.entry.id, candidate.id)}>Vincular e conciliar</button></div>)}
                  {!item.candidates.length && <span>Nenhum lançamento com mesma conta, direção e valor foi localizado.</span>}
                </div>}
              </article>)}

              {group === "systemWithoutBank" && selectedItems.map((movement: any) => <article key={movement.id} className="simple-system-only">
                <MovementSummary movement={movement} label="CCE Gestão"/>
                <span>Não foi encontrado movimento bancário correspondente neste extrato.</span>
              </article>)}

              {!selectedItems.length && <div className="empty-state"><CheckCircle2 size={28}/><strong>Nenhum item neste grupo.</strong></div>}
            </div>
          </SectionCard>

          <SectionCard title="Composição exata da diferença" description="A soma dos itens abaixo deve explicar integralmente a diferença entre o saldo final do CCE e o saldo final do banco.">
            <div className="simple-difference-items">
              {summary.explanation.items.map((item: any) => <article key={item.id}><span><strong>{item.description}</strong><small>{item.problem}</small></span><strong className={item.differenceCents > 0 ? "text-danger" : "text-success"}>{currency(item.differenceCents)}</strong></article>)}
              {!summary.explanation.items.length && <div className="difference-verdict ok"><CheckCircle2 size={20}/><div><strong>Nenhuma diferença</strong><span>Saldo final bancário e saldo final do CCE Gestão conferem.</span></div></div>}
              <div className="simple-difference-total"><span>Total explicado</span><strong>{currency(summary.explanation.totalExplainedCents)}</strong><span>Não explicado</span><strong>{currency(summary.explanation.unexplainedCents)}</strong></div>
            </div>
          </SectionCard>
        </>}
      </div>

      <aside className="reconciliation-history">
        <div className="history-title"><History size={18}/><div><strong>Lotes de extrato</strong><span>Histórico do mesmo fluxo.</span></div></div>
        {imports.map((item) => <button key={item.id} className={current?.id === item.id ? "active" : ""} onClick={() => void openImport(item.id)} disabled={busy === "open"}>
          <span><strong>{item.originalName}</strong><small>{item.bankAccount?.institution} · {dateBr(item.periodStart)} a {dateBr(item.periodEnd)}</small></span>
          <span><StatusBadge status={item.status}/><small>{item.progressPercent}%</small></span>
        </button>)}
        {!imports.length && <div className="history-empty"><Landmark size={24}/><span>Nenhum OFX importado.</span></div>}
      </aside>
    </div>
  </div>;
}

function MovementSummary({ movement, label }: { movement: any; label: string }) {
  if (!movement) return <div className="simple-movement"><span>{label}</span><strong>Sem lançamento</strong></div>;
  return <div className="simple-movement">
    <span>{label}</span>
    <strong>{movement.description}</strong>
    <small>{dateBr(movement.date)}{movement.counterparty ? ` · ${movement.counterparty}` : ""}</small>
    <b className={movement.amountCents >= 0 ? "text-success" : "text-danger"}>{currency(movement.amountCents)}</b>
  </div>;
}
